import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Fab,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  FaBars,
  FaCalendarAlt,
  FaCalendarDay,
  FaChevronCircleDown,
  FaClock,
  FaEdit,
  FaHandHoldingHeart,
  FaHandsHelping,
  FaPlus,
  FaSave,
  FaStickyNote,
  FaTimes,
  FaTrash,
  FaUser
} from 'react-icons/fa';
import { CarePlan, CarePlanFieldPath } from '../../../types/ndisSection/carePlanInfoTypes';

interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  name: CarePlanFieldPath;
  compact?: boolean;
  disabled?: boolean;
  control: any;
  errors: any;
  getValues: () => any;
}

export const InfoCard: React.FC<InfoCardProps> = ({
  icon,
  title,
  name,
  compact = false,
  disabled = false,
  control,
  errors,
  getValues
}) => {
  const theme = useTheme();
  const [fieldEditMode, setFieldEditMode] = useState(false);

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  return (
    <Card
      sx={{
        mb: compact ? 0.5 : 1,
        borderRadius: 1,
        boxShadow: theme.shadows[1],
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent sx={{ p: compact ? 0.75 : 1, display: 'flex', alignItems: 'center' }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.dark,
              width: compact ? 28 : 36,
              height: compact ? 28 : 36,
              mt: compact ? 0.25 : 0
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant={compact ? 'caption' : 'subtitle2'}
                color="textSecondary"
                sx={{ lineHeight: 1.1 }}
              >
                {title}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFieldEditMode(!fieldEditMode)}
                sx={{ ml: 'auto' }}
              >
                <FaEdit style={{ fontSize: '14px' }} />
              </IconButton>
            </Box>
            {fieldEditMode ? (
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!getNestedValue(errors, name)}
                    helperText={getNestedValue(errors, name)?.message}
                    disabled={disabled}
                  />
                )}
              />
            ) : (
              <Typography
                variant={compact ? 'body2' : 'body1'}
                sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}
              >
                {getNestedValue(getValues(), name) ?? '-'}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface SectionHeaderProps {
  icon: JSX.Element;
  title: string;
  color: string;
  editMode: boolean;
  handleEditToggle: () => void;
  handleSubmit: (data: CarePlan, e?: React.BaseSyntheticEvent) => void;
  handleCancel: () => void;
  getValues: () => CarePlan;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon,
  title,
  color,
  editMode,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  getValues
}) => {
  const theme = useTheme();

  const loginInfo = localStorage?.getItem('user-details');
  const userDetails = loginInfo ? JSON?.parse(loginInfo) : null;

  const selectedProject = JSON?.parse(localStorage?.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r?.label?.toLowerCase() === 'admin');

  const onSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    const data = getValues();
    console.log('SectionHeader onSubmit clicked', data, e);
    handleSubmit(data, { nativeEvent: e } as React.BaseSyntheticEvent);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {React.cloneElement(icon, {
          style: { color, fontSize: '20px' }
        })}
        <Typography variant="h6" sx={{ color }}>
          {title}
        </Typography>
      </Box>
      {isAdminLogin && (
        <Box>
          {editMode ? (
            <>
              <IconButton onClick={onSubmit} color="primary" aria-label="save" sx={{ mr: 1 }}>
                <FaSave />
              </IconButton>
              <IconButton onClick={handleCancel} color="secondary" aria-label="cancel">
                <FaTimes />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={handleEditToggle} color="primary" aria-label="edit">
              <FaEdit />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  color,
  children,
  defaultOpen = true
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ mb: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          borderRadius: 1,
          backgroundColor: open ? `${color}08` : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: `${color}12`
          }
        }}
        onClick={() => setOpen(!open)}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color }}>
            {React.cloneElement(icon as JSX.Element, { style: { fontSize: '16px' } })}
          </Box>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <IconButton size="small">
            <FaChevronCircleDown
              style={{ fontSize: '16px', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          </IconButton>
        </Stack>
      </Paper>
      <Collapse in={open}>
        <Box sx={{ pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

interface ArrayFieldProps {
  name: CarePlanFieldPath;
  label: string;
  icon: React.ReactNode;
  color: string;
  items: string[];
  setItems: React.Dispatch<React.SetStateAction<string[]>>;
  editMode: boolean;
  errors: any;
}

export const ArrayField: React.FC<ArrayFieldProps> = ({
  name,
  label,
  icon,
  color,
  items,
  setItems,
  editMode,
  errors
}) => {
  const theme = useTheme();
  const isMobile = theme.breakpoints.down('sm');

  const handleAddStringItem = () => {
    setItems((prev) => [...prev, '']);
  };

  const handleRemoveStringItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateStringItem = (index: number, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  return (
    <CollapsibleSection title={label} icon={icon} color={color}>
      <Stack direction="column" spacing={0.5} sx={{ pl: 1.5 }}>
        {items.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editMode ? (
              <>
                <TextField
                  value={item}
                  onChange={(e) => handleUpdateStringItem(index, e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!getNestedValue(errors, `${name}.${index}`)}
                  helperText={getNestedValue(errors, `${name}.${index}`)?.message}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveStringItem(index)}
                  disabled={items.length === 1}
                >
                  <FaTrash style={{ fontSize: '14px' }} />
                </IconButton>
              </>
            ) : (
              <Chip
                label={item || '-'}
                size={isMobile ? 'small' : 'medium'}
                color="primary"
                variant="outlined"
                icon={React.cloneElement(icon as JSX.Element, { style: { fontSize: '12px' } })}
              />
            )}
          </Box>
        ))}
        {editMode && (
          <Button
            variant="outlined"
            startIcon={<FaPlus />}
            onClick={handleAddStringItem}
            sx={{ mt: 1, width: 'fit-content' }}
          >
            Add {label}
          </Button>
        )}
      </Stack>
    </CollapsibleSection>
  );
};

interface DirectedSupportPlanCardProps {
  index: number;
  removePlan: (index: number) => void;
  editMode: boolean;
  control: any;
  errors: any;
  getValues: () => any;
}

export const DirectedSupportPlanCard: React.FC<DirectedSupportPlanCardProps> = ({
  index,
  removePlan,
  editMode,
  control,
  errors,
  getValues
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 1,
        boxShadow: theme.shadows[2],
        borderLeft: `3px solid ${theme.palette.secondary.main}`
      }}
    >
      <CardContent sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="subtitle1"
            color="secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <FaHandHoldingHeart style={{ fontSize: '16px' }} /> Directed Support Plan {index + 1}
          </Typography>
          {editMode && (
            <IconButton color="error" onClick={() => removePlan(index)}>
              <FaTrash style={{ fontSize: '14px' }} />
            </IconButton>
          )}
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoCard
              icon={<FaHandsHelping />}
              title="Care Need"
              name={`DirectedSupportPlan.${index}.CareNeed` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoCard
              icon={<FaCalendarAlt />}
              title="Frequency"
              name={`DirectedSupportPlan.${index}.FrequencyOfServices` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoCard
              icon={<FaClock />}
              title="Duration"
              name={`DirectedSupportPlan.${index}.VisitTimeDuration` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12}>
            <InfoCard
              icon={<FaStickyNote />}
              title="Actions"
              name={`DirectedSupportPlan.${index}.ActionStrategies` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoCard
              icon={<FaUser />}
              title="Responsible"
              name={`DirectedSupportPlan.${index}.ResponsiblePerson` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoCard
              icon={<FaCalendarDay />}
              title="By When"
              name={`DirectedSupportPlan.${index}.ByWhen` as CarePlanFieldPath}
              control={control}
              errors={errors}
              getValues={getValues}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

interface MobileNavButtonProps {
  toggleDrawer: () => void;
  drawerOpen: boolean;
}

export const MobileNavButton: React.FC<MobileNavButtonProps> = ({ toggleDrawer, drawerOpen }) => {
  const theme = useTheme();

  return (
    <Fab
      color="primary"
      aria-label="menu"
      onClick={toggleDrawer}
      sx={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        display: { xs: 'flex', sm: 'none' },
        zIndex: theme.zIndex.speedDial,
        width: 48,
        height: 48
      }}
    >
      {drawerOpen ? (
        <FaTimes style={{ fontSize: '20px' }} />
      ) : (
        <FaBars style={{ fontSize: '20px' }} />
      )}
    </Fab>
  );
};

interface TextFieldProps {
  label: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  name: CarePlanFieldPath;
  control: any; // Control<CarePlan>;
  errors: any; // FieldErrors<CarePlan>;
  getValues: any; // UseFormGetValues<CarePlan>;
  icon?: React.ReactElement;
  multiline?: boolean;
  minRows?: number;
}

export const CareTextField: React.FC<TextFieldProps> = ({
  label,
  name,
  control,
  errors,
  getValues,
  icon,
  size,
  multiline,
  placeholder,
  minRows
}) => {
  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label={label}
          multiline={multiline}
          minRows={minRows}
          size={size}
          variant="outlined"
          fullWidth
          margin="normal"
          error={!!errorMessage}
          helperText={errorMessage}
          InputProps={{
            startAdornment: icon ? (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            ) : null
          }}
        />
      )}
    />
  );
};
